import { useState, useCallback } from 'react';

export type MediaItem = {
  id: string;
  mediaType: 'image' | 'video';
  mediaUrl: string;
  caption?: string;
  isPrimary: boolean;
  displayOrder: number;
  isNew?: boolean; // Flag for newly uploaded items not yet saved
};

export type MediaGalleryState = {
  items: MediaItem[];
  isModalOpen: boolean;
};

type UseMediaGalleryOptions = {
  initialItems?: MediaItem[];
  /** Callback when items change - for syncing to form */
  onChange?: (items: MediaItem[]) => void;
};

/**
 * useMediaGallery - Custom hook for managing media gallery state
 * 
 * Features:
 * - Add/remove media items
 * - Reorder items via drag-drop
 * - Set primary image (auto-unmarks others)
 * - Update captions
 * - Track new vs existing items
 * - Modal open/close state
 * - Optional onChange callback for form sync
 * 
 * Usage:
 * Used by ProfileMediaSection and MediaGalleryModal to manage
 * the provider_media relationship with proper ordering and primary selection
 */
export function useMediaGallery(initialItems: MediaItem[], onChange?: (items: MediaItem[]) => void): ReturnType<typeof useMediaGalleryImpl>;
export function useMediaGallery(options: UseMediaGalleryOptions): ReturnType<typeof useMediaGalleryImpl>;
export function useMediaGallery(
  initialItemsOrOptions: MediaItem[] | UseMediaGalleryOptions = [],
  onChangeParam?: (items: MediaItem[]) => void
) {
  // Handle both function signatures
  const options = Array.isArray(initialItemsOrOptions)
    ? { initialItems: initialItemsOrOptions, onChange: onChangeParam }
    : initialItemsOrOptions;

  return useMediaGalleryImpl(options);
}

function useMediaGalleryImpl({ initialItems = [], onChange }: UseMediaGalleryOptions) {
  const [items, setItems] = useState<MediaItem[]>(initialItems);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Add new media item to gallery
   */
  const addItem = useCallback((item: Omit<MediaItem, 'displayOrder' | 'isPrimary' | 'id'>) => {
    setItems((prev) => {
      const maxOrder = prev.length > 0 ? Math.max(...prev.map(i => i.displayOrder)) : -1;
      const newItem: MediaItem = {
        ...item,
        id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for new items
        displayOrder: maxOrder + 1,
        isPrimary: prev.length === 0, // First item is primary by default
        isNew: true,
      };
      const newItems = [...prev, newItem];
      if (onChange) onChange(newItems);
      return newItems;
    });
  }, [onChange]);

  /**
   * Remove media item from gallery
   */
  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => {
      const filtered = prev.filter(item => item.id !== itemId);
      
      // If we removed the primary item, make the first item primary
      const hasPrimary = filtered.some(item => item.isPrimary);
      if (!hasPrimary && filtered.length > 0) {
        filtered[0].isPrimary = true;
      }
      
      // Recalculate display order
      const newItems = filtered.map((item, index) => ({
        ...item,
        displayOrder: index,
      }));
      if (onChange) onChange(newItems);
      return newItems;
    });
  }, [onChange]);

  /**
   * Reorder items after drag-drop
   */
  const reorderItems = useCallback((startIndex: number, endIndex: number) => {
    setItems((prev) => {
      const result = Array.from(prev);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      
      // Update display order
      const newItems = result.map((item, index) => ({
        ...item,
        displayOrder: index,
      }));
      if (onChange) onChange(newItems);
      return newItems;
    });
  }, [onChange]);

  /**
   * Set primary image (auto-unmarks all others)
   */
  const setPrimary = useCallback((itemId: string) => {
    setItems((prev) => {
      const newItems = prev.map(item => ({
        ...item,
        isPrimary: item.id === itemId,
      }));
      if (onChange) onChange(newItems);
      return newItems;
    });
  }, [onChange]);

  /**
   * Update item caption
   */
  const updateCaption = useCallback((itemId: string, caption: string) => {
    setItems((prev) => {
      const newItems = prev.map(item =>
        item.id === itemId ? { ...item, caption } : item
      );
      if (onChange) onChange(newItems);
      return newItems;
    });
  }, [onChange]);

  /**
   * Open gallery modal
   */
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  /**
   * Close gallery modal
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * Reset gallery to new items
   */
  const reset = useCallback((newItems: MediaItem[] = []) => {
    setItems(newItems);
    if (onChange) onChange(newItems);
  }, [onChange]);

  /**
   * Get primary item
   */
  const primaryItem = items.find(item => item.isPrimary);

  return {
    items,
    primaryItem,
    isModalOpen,
    addItem,
    removeItem,
    reorderItems,
    setPrimary,
    updateCaption,
    openModal,
    closeModal,
    reset,
  };
}

